import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useEffect, useState } from "react";
import { SITE } from "@/data/site";

const links = [
  { to: "/", label: "Home", tid: "nav-home" },
  { to: "/work", label: "Work", tid: "nav-work" },
  { to: "/about", label: "About", tid: "nav-about" },
  { to: "/contact", label: "Contact", tid: "nav-contact" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);

  return (
    <header
      data-testid="site-nav"
      className="sticky top-0 z-40 border-b border-zinc-900 bg-white/95 backdrop-blur"
    >
      <div className="grid grid-cols-12 items-center">
        <Link
          to="/"
          data-testid="nav-logo"
          className="col-span-7 sm:col-span-8 md:col-span-3 flex items-center gap-2 px-4 md:px-5 py-3 md:py-4 border-r border-zinc-900 min-h-[52px]"
        >
          <span className="inline-block w-3 h-3 shrink-0 bg-[#FF1111]" />
          <span className="font-display text-base md:text-xl leading-none tracking-tight uppercase truncate">
            {SITE.name}
          </span>
        </Link>

        <nav className="hidden md:flex col-span-5 items-center justify-center gap-8 lg:gap-12 border-r border-zinc-900 h-full min-h-[52px]">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.tid}
              end={l.to === "/"}
              className={({ isActive }) =>
                `link-slash font-mono text-sm font-bold uppercase tracking-[0.15em] transition-colors duration-200 ${
                  isActive ? "text-[#FF1111]" : "text-zinc-900 hover:text-[#FF1111]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex col-span-4 items-stretch justify-end min-h-[52px]">
          <a
            data-testid="nav-ig"
            href={SITE.instagramUrl}
            target="_blank"
            rel="noreferrer"
            className="font-mono text-sm font-bold uppercase tracking-[0.15em] hover:text-[#FF1111] hover:bg-zinc-50 transition-all duration-200 inline-flex items-center px-5 whitespace-nowrap"
          >
            @{SITE.instagram}
          </a>
          <Link
            to="/contact"
            data-testid="nav-cta"
            className="bg-[#FF1111] text-white px-5 font-mono text-sm font-bold uppercase tracking-[0.15em] hover:bg-black transition-all duration-200 whitespace-nowrap inline-flex items-center"
          >
            Start a project
          </Link>
        </div>

        <button
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          className="md:hidden col-span-5 sm:col-span-4 flex items-center justify-end gap-2 px-4 py-3 border-l border-zinc-900 min-h-[52px]"
          aria-label="Toggle menu"
          aria-expanded={open}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="md:hidden border-t border-zinc-900 bg-white"
        >
          <div className="flex flex-col divide-y divide-zinc-900">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`m-${l.tid}`}
                className={({ isActive }) =>
                  `px-5 py-4 font-display uppercase text-2xl min-h-[56px] flex items-center ${
                    isActive ? "text-[#FF1111]" : "text-zinc-900"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={SITE.instagramUrl}
              className="px-5 py-4 font-mono text-xs uppercase tracking-[0.2em] min-h-[52px] flex items-center"
              data-testid="m-nav-ig"
              target="_blank"
              rel="noreferrer"
            >
              Instagram → @{SITE.instagram}
            </a>
            <Link
              to="/contact"
              className="px-5 py-4 bg-[#FF1111] text-white font-mono text-xs uppercase tracking-[0.2em] min-h-[52px] flex items-center justify-between"
            >
              Work with us <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
